import { Field, TextOutput } from 'Components/Core';
import { NamePrefixModifier, withInjectedProps } from 'Components/Core/InjectedPropsContext';
import { PageSection } from 'Components/Layout';
import { IWorksheetComponent } from 'Components/Worksheets/Core';
import { valueOrDefault } from 'Lib/Any';
import { pipe } from 'Lib/Hooks/Selectors';
import React from 'react';
import { useSelector } from 'react-redux';
import { TestProcedure } from '../../types';
import CoarsePortionSievingMassesSection from './CoarsePortionSievingMassesSection';
import FinePortionSievingMassesSection from './FinePortionSievingMassesSection';
import IntermediatePortionSievingMassesSection from './IntermediatePortionSievingMassesSection';
import PortionSievingEquipmentSection from './PortionSievingEquipmentSection';
import PortionSievingRecordGridSection from './PortionSievingRecordGridSection';
import WholePortionSievingMassesSection from './WholePortionSievingMassesSection';

let PortionSievingPageSection: React.FC<IWorksheetComponent> = ({ createWorksheetSelector, namePrefix }) => {
  const isWholePortion = namePrefix!.endsWith('WholePortion');
  const isCoarsestSievingPortion = namePrefix!.endsWith('WholePortion.Separation.CoarsePortion');
  const isFineSievingPortion = namePrefix!.endsWith('FinePortion.Subspecimen');
  const isIntermediatePortion = !isCoarsestSievingPortion && namePrefix!.endsWith('CoarsePortion');

  // Portion number for intermediate portions. Since we only use it for intermediate portions, and
  // we support a maxmimum of 2 separations, the only case that we actually end up using it for is
  // the coarse portion of the second separation, and we expect the value to be 2 for this case.
  const intermediatePortionNumber = isIntermediatePortion ? namePrefix?.match(/\.Separation\./g)?.length ?? 0 : 0;

  const heading = isWholePortion
    ? 'Portion Sieving'
    : isCoarsestSievingPortion
    ? 'Coarse Portion Sieving'
    : isFineSievingPortion
    ? 'Fine Portion Sieving'
    : `Portion ${intermediatePortionNumber} Sieving`;

  const initialSeparationPerformed: boolean = useSelector(
    pipe(createWorksheetSelector!('data.Preparation.InitialSeparationPerformed', false), valueOrDefault(false))
  );

  const isAnnexA = useSelector(
    pipe(createWorksheetSelector!('data.Preparation.Method', false), (m) => m === TestProcedure.AnnexA)
  );

  const isCoarsePortionOfSeparationDuringSieving =
    (isCoarsestSievingPortion && !initialSeparationPerformed) || isIntermediatePortion;

  return (
    <PageSection heading={heading}>
      {isWholePortion && <WholePortionSievingMassesSection />}
      {isCoarsestSievingPortion && <CoarsePortionSievingMassesSection />}
      {isIntermediatePortion && <IntermediatePortionSievingMassesSection />}
      {isFineSievingPortion && <FinePortionSievingMassesSection />}

      <NamePrefixModifier appendNamePrefix="Sieving">
        <PortionSievingEquipmentSection />
        <PortionSievingRecordGridSection
          isCoarsePortionOfSeparationDuringSieving={isCoarsePortionOfSeparationDuringSieving}
        />

        {isCoarsePortionOfSeparationDuringSieving && (
          <Field input={<TextOutput />} name="ExpectedIndividualPanMassRetained" />
        )}

        {isAnnexA && isFineSievingPortion && <Field input={<TextOutput />} name="UpscaledPanMass" />}
      </NamePrefixModifier>
    </PageSection>
  );
};
PortionSievingPageSection = withInjectedProps(PortionSievingPageSection);

export { PortionSievingPageSection };
export default PortionSievingPageSection;
